import { LANGUAGE_PATHS } from './brand.ts';

/**
 * The site's content pages beside the app: one landing page per job people
 * search for, comparisons with other tools, and the privacy policy. Addresses
 * live here because the app links to them (home footer) and the build writes
 * them (static HTML, sitemap, llms.txt). Plain constants only, like brand.ts.
 *
 * `recipe` is the built-in recipe the page's button opens the app with, or
 * `app` what else it opens with (the metadata inspector).
 * Addresses are permanent once indexed: change one only together with a redirect.
 */
export const SITE_PAGES = {
  faces: { en: '/blur-faces-in-video/', tr: '/tr/videoda-yuz-bulaniklastirma/', recipe: 'faces' },
  pixelate: { en: '/pixelate-face-in-video/', tr: '/tr/videoda-yuz-piksellestirme/', recipe: 'faces' },
  moving: { en: '/blur-moving-face-in-video/', tr: '/tr/hareketli-yuz-bulaniklastirma/', recipe: 'faces' },
  kids: { en: '/blur-childrens-faces-in-video/', tr: '/tr/cocuk-yuzu-bulaniklastirma/', recipe: 'faces' },
  plates: { en: '/blur-license-plates/', tr: '/tr/videoda-plaka-bulaniklastirma/', recipe: 'plates' },
  dashcam: { en: '/dashcam-video-editor/', tr: '/tr/arac-kamerasi-video-duzenleme/', recipe: 'privacy' },
  // Every export leaves the location behind, so this job needs nothing applied (and no model downloaded).
  location: { en: '/remove-location-from-video/', tr: '/tr/videodan-konum-bilgisi-silme/', recipe: 'blank' },
  silence: { en: '/remove-silence-from-video/', tr: '/tr/videodaki-sessizlikleri-kesme/', recipe: 'silence' },
  captions: { en: '/add-captions-to-video/', tr: '/tr/videoya-altyazi-ekleme/', recipe: 'captions' },
  srt: { en: '/srt-subtitle-generator/', tr: '/tr/srt-altyazi-olusturma/', recipe: 'subtitles' },
  resize: { en: '/resize-video-for-reels-tiktok/', tr: '/tr/videoyu-dikey-yapma/', recipe: 'reels' },
  merge: { en: '/merge-videos/', tr: '/tr/video-birlestirme/', recipe: 'youtube' },
  // Shapes are chosen per clip in the plain editor, so nothing else is applied.
  mixed: { en: '/merge-portrait-and-landscape-videos/', tr: '/tr/dikey-ve-yatay-video-birlestirme/', recipe: 'blank' },
  music: { en: '/add-music-to-video/', tr: '/tr/videoya-muzik-ekleme/', recipe: 'reels' },
  speedup: { en: '/speed-up-video/', tr: '/tr/video-hizlandirma/', recipe: 'youtube' },
  screen: { en: '/screen-recorder/', tr: '/tr/ekran-kaydedici/', recipe: 'recording' },
  motovlog: { en: '/motovlog-editor/', tr: '/tr/motovlog-video-duzenleme/', recipe: 'motovlog' },
  gopro: { en: '/merge-gopro-videos/', tr: '/tr/gopro-videolarini-birlestirme/', recipe: 'motovlog' },
  speed: { en: '/gopro-speed-overlay/', tr: '/tr/gopro-hiz-gostergesi/', recipe: 'motovlog' },
  wind: { en: '/remove-wind-noise-from-video/', tr: '/tr/videoda-ruzgar-sesi-giderme/', recipe: 'motovlog' },
  metadata: { en: '/video-metadata-viewer/', tr: '/tr/video-exif-bilgileri/', app: { inspect: true } },
  findlocation: { en: '/check-video-location/', tr: '/tr/videodan-konum-bulma/', app: { inspect: true } },
  capcut: { en: '/capcut-alternative/', tr: '/tr/capcut-alternatifi/' },
  veed: { en: '/veed-alternative/', tr: '/tr/veed-alternatifi/' },
  kapwing: { en: '/kapwing-alternative/', tr: '/tr/kapwing-alternatifi/' },
  blurme: { en: '/blurme-alternative/', tr: '/tr/blurme-alternatifi/' },
  descript: { en: '/descript-alternative/', tr: '/tr/descript-alternatifi/' },
  clipchamp: { en: '/clipchamp-alternative/', tr: '/tr/clipchamp-alternatifi/' },
  flexclip: { en: '/flexclip-alternative/', tr: '/tr/flexclip-alternatifi/' },
  inshot: { en: '/inshot-alternative/', tr: '/tr/inshot-alternatifi/' },
  clideo: { en: '/clideo-alternative/', tr: '/tr/clideo-alternatifi/' },
  loom: { en: '/loom-alternative/', tr: '/tr/loom-alternatifi/' },
  obs: { en: '/obs-alternative/', tr: '/tr/obs-alternatifi/' },
  screenpal: { en: '/screenpal-alternative/', tr: '/tr/screenpal-alternatifi/' },
  handbrake: { en: '/handbrake-alternative/', tr: '/tr/handbrake-alternatifi/' },
  cloudconvert: { en: '/cloudconvert-alternative/', tr: '/tr/cloudconvert-alternatifi/' },
  freeconvert: { en: '/freeconvert-alternative/', tr: '/tr/freeconvert-alternatifi/' },
  privacy: { en: '/privacy/', tr: '/tr/gizlilik/' },
} as const;

export type SitePageKey = keyof typeof SITE_PAGES;
export type SiteLanguage = keyof typeof LANGUAGE_PATHS;

/** Pages that present a job the app does, in the order they are linked: privacy first, then sound, format, putting a video together, and rides. */
export const TOOL_PAGES = [
  'faces',
  'pixelate',
  'moving',
  'kids',
  'plates',
  'dashcam',
  'location',
  'metadata',
  'findlocation',
  'silence',
  'captions',
  'srt',
  'resize',
  'merge',
  'mixed',
  'music',
  'speedup',
  'screen',
  'motovlog',
  'gopro',
  'speed',
  'wind',
] as const satisfies readonly SitePageKey[];
export type ToolPageKey = (typeof TOOL_PAGES)[number];

/** Comparisons with other tools ("… alternative"). */
export const COMPARE_PAGES = [
  'capcut',
  'veed',
  'kapwing',
  'blurme',
  'descript',
  'clipchamp',
  'flexclip',
  'inshot',
  'clideo',
  'loom',
  'obs',
  'screenpal',
  'handbrake',
  'cloudconvert',
  'freeconvert',
] as const satisfies readonly SitePageKey[];
export type ComparePageKey = (typeof COMPARE_PAGES)[number];

/** Every content page, in the order they are linked: tools, comparisons, then the policy. */
export const CONTENT_PAGES: readonly SitePageKey[] = [...TOOL_PAGES, ...COMPARE_PAGES, 'privacy'];

/**
 * Hubs of the programmatic templates (content/pseo): one page per template
 * listing its rows. The app's footer links them beside the tools, so the
 * addresses live here; build/pseo.test.ts checks each is a published hub in
 * every language. Permanent once indexed, like the pages above.
 */
export const HUB_PAGES = {
  convert: { en: '/video-converter/', tr: '/tr/video-donusturucu/' },
  compress: { en: '/compress-video/', tr: '/tr/video-sikistirma/' },
  specs: { en: '/video-specs/', tr: '/tr/video-boyutlari/' },
  jobs: { en: '/platform-guides/', tr: '/tr/platform-rehberleri/' },
  filesize: { en: '/video-file-size/', tr: '/tr/video-dosya-boyutu/' },
} as const;

export type HubKey = keyof typeof HUB_PAGES;

/**
 * Query parameters a link can open the app with. The content pages write them
 * ({@link appLink}) and the app reads them in one place (src/ui/home/app-params.ts),
 * so both sides share these names (docs/seo-plan.md §4.3).
 */
export const APP_PARAMS = {
  /** A built-in recipe chosen (`/?for=privacy`). */
  recipe: 'for',
  /** Quick convert: dropped videos go straight to saving in this format (`/?to=mp4`). */
  output: 'to',
  /** Keep the video under this many MB (`/?target=20mb`); alone, it converts to MP4. */
  sizeLimit: 'target',
  /** The metadata inspector: what a dropped video says about where, when and on what it was made (`/?inspect=1`). */
  inspect: 'inspect',
  /** Fit the video for where it will be posted: its shape, length and largest file (`/?fit=instagram-reels`). */
  placement: 'fit',
  /** The language spoken, for the captions (`/?lang=tr`). */
  captionLanguage: 'lang',
  /** Captions translated into English (`/?task=translate`). */
  captionTask: 'task',
} as const;

/** What a link asks the app to open with; every field is optional. */
export interface AppLink {
  readonly recipe?: string;
  /** An output format id (`OUTPUT_FORMATS` in src/core/project/output-format.ts). */
  readonly output?: string;
  /** Megabytes, written as `20mb`. */
  readonly sizeLimit?: number;
  /** Open the metadata inspector, written as `1`. */
  readonly inspect?: boolean;
  /** A placement id (`PLACEMENTS` in src/core/project/placement.ts). */
  readonly placement?: string;
  /** A Whisper language code (`CAPTION_LANGUAGES` in src/core/captions/types.ts). */
  readonly captionLanguage?: string;
  /** Write the captions in English, written as `translate`. */
  readonly captionTask?: 'translate';
}

/** How each field is written in the address. */
const WRITE: { readonly [K in keyof AppLink]-?: (value: NonNullable<AppLink[K]>) => string } = {
  recipe: (recipe) => recipe,
  output: (output) => output,
  sizeLimit: (mb) => `${mb}mb`,
  inspect: () => '1',
  placement: (id) => id,
  captionLanguage: (code) => code,
  captionTask: (task) => task,
};

/** Query parameter that opens the app with a recipe chosen (`/?for=privacy`). */
export const RECIPE_PARAM = APP_PARAMS.recipe;

/** The app's home in a language, with whatever the link asks for (empty fields are left out). */
export function appLink(language: SiteLanguage, link: AppLink = {}): string {
  const query = (Object.keys(APP_PARAMS) as (keyof AppLink)[])
    .filter((field) => link[field])
    .map((field) => `${APP_PARAMS[field]}=${encodeURIComponent((WRITE[field] as (value: unknown) => string)(link[field]))}`)
    .join('&');
  return query ? `${LANGUAGE_PATHS[language]}?${query}` : LANGUAGE_PATHS[language];
}

/** The app's home in a language, optionally with a recipe chosen. */
export const appPath = (language: SiteLanguage, recipe?: string): string => appLink(language, { recipe });

/**
 * Whether a page with this link takes the video right there (src/intake):
 * only when the link sets the app up for a job, so the video can go straight
 * to it. A recording starts from the screen, not from a file, and a link that
 * asks for nothing leaves the choice to the app's home page.
 */
export const takesFiles = (link: AppLink): boolean => link.recipe !== 'recording' && (Object.keys(APP_PARAMS) as (keyof AppLink)[]).some((field) => link[field]);

/** Where a content page's button opens the app: its recipe, or what else it asks for. */
export function pageAppLink(key: SitePageKey): AppLink {
  const page = SITE_PAGES[key];
  if ('app' in page) return page.app;
  return 'recipe' in page ? { recipe: page.recipe } : {};
}
